import moment from "moment";
import "moment/locale/pt-br";

import React, { useEffect, useState, useCallback } from "react"; // Adicionado useCallback

import { useSelector } from "react-redux";
import { useIntl } from "react-intl";

import { Row, Col, DatePicker, Skeleton, Select, Popover } from "antd";
import { MoneyRecive, MoneySend, WalletMinus } from "iconsax-react";
import { RiCalendarLine } from "react-icons/ri";

import ZendeskLogo from "../../../../assets/images/dasboard/zendesk-logo.svg";
import AppleLogo from "../../../../assets/images/dasboard/apple-logo.svg";

import FeatureCard from "./featureCard";
import BalanceCard from "./balanceCard";
import ListCard from "./listCard";
import HistoryCard from "./historyCard";

import {
  getNfe,
  getNfeAnalisesFaturamento,
  getNfeAnalisesProdutos,
} from "../../../../lib/requests";

export default function Analytics() {
  moment.locale("pt-br");

  const customise = useSelector((state) => state.customise);
  const intl = useIntl();

  // Strings traduzidas
  const income = intl.formatMessage({ id: "analytics-income" });
  const expenses = intl.formatMessage({ id: "analytics-expenses" });
  const balance = intl.formatMessage({ id: "analytics-balance" });
  const recent_activities = intl.formatMessage({
    id: "analytics-recent-activities",
  });
  const upcoming_payments = intl.formatMessage({
    id: "analytics-upcoming-payments",
  });

  // Estados de carregamento
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingProdutos, setLoadingProdutos] = useState(true);

  // Estados dos dados
  const [nfeList, setNfeList] = useState([]);
  const [analise, setAnalise] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment()); // Inicializa com o momento atual
  const [produtos, setProdutos] = useState([]);
  const [productFilter, setProductFilter] = useState("mais_comprados");

  // --- Funções de Busca da API (Memorizadas com useCallback) ---

  const fetchNfes = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await getNfe();
      if (response.error) {
        console.error("Erro ao buscar NF-e:", response.error.message);
        return;
      }
      const list = response.data.results.map((item) => {
        const totalProdutos = item.produtos?.reduce(
          (acc, prod) => acc + parseFloat(prod.vProd || 0),
          0
        );
        return {
          id: item?.id,
          name: item.emitente?.xNome || "Emitente desconhecido",
          title: item.ide?.natOp || "Sem natureza",
          price: totalProdutos?.toFixed(2) || "0.00",
          number: item.ide?.nNF || "—",
          version: `v${item.versao || "N/A"}`,
          fileXml: item.fileXml,
        };
      });
      setNfeList(list);
    } catch (err) {
      console.error("Erro inesperado ao buscar NF-e:", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []); // Array de dependência vazio significa que esta função é criada uma vez

  const fetchAnaliseFaturamento = useCallback(async (date) => {
    if (!date) return; // Evita a chamada se a data for nula
    const mes = date.month() + 1; // month() do moment é baseado em 0
    const ano = date.year();

    setLoadingAnalytics(true);
    try {
      const response = await getNfeAnalisesFaturamento({ mes, ano });
      if (response.error) {
        console.error(
          "Erro na análise de faturamento:",
          response.error.message
        );
        return;
      }
      setAnalise(response.data);
    } catch (err) {
      console.error("Erro inesperado na análise de faturamento:", err);
    } finally {
      setLoadingAnalytics(false);
    }
  }, []); // Array de dependência vazio

  const fetchProdutos = useCallback(async (filter) => {
    setLoadingProdutos(true);
    try {
      const response = await getNfeAnalisesProdutos({
        order: filter,
        limit: 6,
      });
      if (response.error) {
        console.error("Erro ao buscar produtos:", response.error.message);
        return;
      }
      const produtosList = response.data.map((item) => ({
        img: item.cean === "SEM GTIN" ? ZendeskLogo : AppleLogo,
        title: item.xprod,
        date: `${item.qtd_total} unidades`,
        preco_medio: Number(item.preco_medio),
        preco_min: Number(item.preco_min),
        preco_max: Number(item.preco_max),
        variacao: Number(item.variacao_preco),
      }));
      setProdutos(produtosList);
    } catch (err) {
      console.error("Erro inesperado ao buscar produtos:", err);
    } finally {
      setLoadingProdutos(false);
    }
  }, []); // Array de dependência vazio

  // --- Efeitos para acionar a busca de dados ---

  // Efeito para buscar a lista de NF-e na montagem do componente
  useEffect(() => {
    fetchNfes();
  }, [fetchNfes]); // Dependência: função fetchNfes memorizada

  // Efeito para buscar a Análise de Faturamento quando selectedDate muda
  useEffect(() => {
    fetchAnaliseFaturamento(selectedDate);
  }, [selectedDate, fetchAnaliseFaturamento]); // Dependências: selectedDate e fetchAnaliseFaturamento memorizada

  // Efeito para buscar Produtos quando productFilter muda
  useEffect(() => {
    fetchProdutos(productFilter);
  }, [productFilter, fetchProdutos]); // Dependências: productFilter e fetchProdutos memorizada

  // --- Handlers para interações do usuário ---

  const handleDateChange = (value) => {
    if (value) {
      setSelectedDate(value);
    }
  };

  const handleProductFilterChange = (filter) => {
    setProductFilter(filter);
  };

  const content_income = (
    <div>
      <p className="hp-mb-0">
        Valor total faturado no mês selecionado com <br />
        base nas NF-e emitidas.
        <br />
        Este número reflete o volume bruto de vendas <br />e serve como
        principal indicador de desempenho mensal.
      </p>
    </div>
  );

  const content_expenses = (
    <div>
      <p className="hp-mb-0">
        Valor da maior nota fiscal emitida no mês. <br />
        Esse dado pode indicar grandes pedidos <br />
        isolados, oportunidades de fidelização ou <br />
        necessidade de atenção especial ao cliente envolvido.
      </p>
    </div>
  );

  const content_balance = (
    <div>
      <p className="hp-mb-0">
        Média de faturamento mensal considerando o <br />
        período completo analisado.
        <br />
        Ideal para comparar sazonalidade, crescimento <br />
        ou retração nas vendas.
      </p>
    </div>
  );

  // --- Seção de Renderização ---
  return (
    <Row gutter={[64, 64]} className="hp-mb-32">
      <Col flex="1" className="hp-overflow-hidden">
        <Row gutter={[0, 16]} align="middle" justify="space-between">
          {loadingAnalytics ? (
            <Col sm={8} span={24}>
              <Skeleton active />
            </Col>
          ) : (
            <>
              <Col sm={12} span={24}>
                <span className="h3 hp-font-weight-700 hp-text-color-black-bg hp-text-color-dark-0 hp-d-block">
                  Nfe emitidas
                </span>
                <span className="hp-p1-body hp-mt-4 hp-mb-0">
                  total {analise?.quantidade_notas_mes || 0}
                </span>
              </Col>

              <Col>
                <DatePicker
                  onChange={handleDateChange} // Usa o novo handler
                  picker="month"
                  format="MM/YYYY"
                  value={selectedDate} // Garante que o DatePicker é controlado pelo estado
                  suffixIcon={
                    <RiCalendarLine className="remix-icon hp-text-color-black-100" />
                  }
                  className="hp-w-100"
                />
              </Col>
            </>
          )}
        </Row>

        <Row gutter={[32, 32]} className="hp-mt-24">
          <Col span={24}>
            <Row gutter={[16, 16]}>
              {loadingAnalytics ? (
                // Exibindo o Skeleton quando estiver carregando
                <>
                  <Col sm={8} span={24}>
                    <Skeleton active />
                  </Col>
                  <Col sm={8} span={24}>
                    <Skeleton active />
                  </Col>
                  <Col sm={8} span={24}>
                    <Skeleton active />
                  </Col>
                </>
              ) : (
                <>
                  <Popover
                    content={content_income}
                    title={income}
                    trigger="hover"
                  >
                    <Col sm={8} span={24}>
                      <FeatureCard
                        icon={
                          <MoneyRecive
                            size="24"
                            variant="Bold"
                            className="hp-text-color-black-bg hp-text-color-dark-0"
                          />
                        }
                        title={income}
                        date={selectedDate?.format("MMMM YYYY")}
                        price={analise?.total_mes || "0.00"}
                      />
                    </Col>
                  </Popover>

                  <Popover
                    content={content_expenses}
                    title={expenses}
                    trigger="hover"
                  >
                    <Col sm={8} span={24}>
                      <FeatureCard
                        icon={
                          <MoneySend
                            size="24"
                            variant="Bold"
                            className="hp-text-color-black-bg hp-text-color-dark-0"
                          />
                        }
                        title={expenses}
                        date={selectedDate?.format("MMMM YYYY")}
                        price={analise?.maior_nota_mes || "0.00"}
                      />
                    </Col>
                  </Popover>

                  <Popover
                    content={content_balance}
                    title={balance}
                    trigger="hover"
                  >
                    <Col sm={8} span={24}>
                      <FeatureCard
                        icon={
                          <WalletMinus
                            size="24"
                            variant="Bold"
                            className="hp-text-color-black-bg hp-text-color-dark-0"
                          />
                        }
                        title={balance}
                        date={selectedDate?.format("MMMM YYYY")}
                        price={analise?.media_mensal || "0.00"}
                      />
                    </Col>
                  </Popover>
                </>
              )}
            </Row>
          </Col>

          <Col span={24}>
            {loadingAnalytics ? (
              <Skeleton active />
            ) : (
              <BalanceCard
                data={analise}
                loading={loadingAnalytics}
                // DatePicker é tratado no nível superior agora para consistência
              />
            )}
          </Col>

          {loadingHistory ? (
            // Exibindo o Skeleton quando estiver carregando
            <Col span={24}>
              <Skeleton active />
            </Col>
          ) : (
            <Col span={24}>
              <HistoryCard list={nfeList} title={recent_activities} />
            </Col>
          )}
        </Row>
      </Col>

      {customise.contentWidth === "boxed" && (
        <Col className="hp-dashboard-line hp-px-0">
          <div
            className="hp-bg-black-40 hp-bg-dark-80 hp-h-100 hp-mx-24"
            style={{ width: 1 }}
          ></div>
        </Col>
      )}

      <Col
        className={`hp-analytics-col-2${
          customise.contentWidth && " hp-boxed-active"
        }`}
      >
        <Row gutter={[32, 40]}>
          {loadingProdutos ? (
            // Exibindo o Skeleton quando estiver carregando
            <Col span={24}>
              <Skeleton active />
            </Col>
          ) : (
            <Col span={24}>
              <ListCard
                title={upcoming_payments}
                // Nenhuma data fixa, ou derive dos dados de produtos se disponível
                list={produtos}
                select={
                  <Select
                    onChange={handleProductFilterChange} // Usa o novo handler
                    value={productFilter}
                    style={{ width: 150 }}
                  >
                    <Select.Option value="mais_comprados">
                      Mais Comprados
                    </Select.Option>
                    <Select.Option value="mais_caros">Mais caros</Select.Option>
                    <Select.Option value="mais_instaveis">
                      Mais instáveis
                    </Select.Option>
                  </Select>
                }
              />
            </Col>
          )}

          <Col span={24}>
            <ListCard
              title={recent_activities} // Este título parece duplicado, considere renomear ou remover este cartão se seu propósito não for distinto
              date="05 Dec 2021" // Esta é uma data hardcoded, você pode querer buscar datas de atividades recentes reais
              list={
                [] // Sua lista está comentada, certifique-se de que este componente serve a um propósito distinto ou remova-o.
              }
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
